import { useUniqueIds } from '@/composables/useUniqueIds';
import { cloneProxyObject } from '@/utils/cloneProxyObject';
import { NONE } from '@/enums/indicators-and-overlays';
import { DEFAULT_INDICATOR_POSITION } from '@/enums/indicator-positions';
import { DEFAULT_PRICE_INDICATOR_STYLE_OPTION } from '@/enums/price-indicator-style-options';
import { UP_DOWN_COLOR_OPTION_AUTO } from '@/enums/up-down-color-options';
import { DEFAULT_INDICATOR_HEIGHT_OPTION } from '@/enums/indicator-height-options';
import { OPACITY_OPTION_AUTO } from '@/enums/opacity-options';
import { OVERLAY_STYLE_AUTO } from '@/enums/overlay-styles';
import { DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL } from '@/enums/symbols';
import { Indicator, Overlay } from '@/interfaces/chart';

const INDICATOR_TEMPLATE = {
  indicatorName: NONE,
  parameters: '',
  indicatorPosition: DEFAULT_INDICATOR_POSITION.title,
  indicatorPriceStyle: DEFAULT_PRICE_INDICATOR_STYLE_OPTION.title,
  indicatorColor: UP_DOWN_COLOR_OPTION_AUTO,
  indicatorHeight: DEFAULT_INDICATOR_HEIGHT_OPTION.title,
  indicatorOpacity: OPACITY_OPTION_AUTO.title,
  overlayName: NONE,
  overlayParameters: ''
};

const OVERLAY_TEMPLATE = {
  overlayName: NONE,
  parameters: '',
  overlayStyle: OVERLAY_STYLE_AUTO.title,
  overlayColor: UP_DOWN_COLOR_OPTION_AUTO,
  overlayOpacity: OPACITY_OPTION_AUTO.title
};

const MAXIMUM_NUMBER_OF_INDICATORS = 25;
const MAXIMUM_NUMBER_OF_INDICATORS_FOR_FREE_USERS = 3;
const MAXIMUM_NUMBER_OF_OVERLAYS = 25;
const MAXIMUM_NUMBER_OF_OVERLAYS_FOR_FREE_USERS = 3;

function getBlankIndicator() {
  const { getPositiveUniqueId } = useUniqueIds();

  return {
    ...INDICATOR_TEMPLATE,
    id: getPositiveUniqueId()
  };
}

function getBlankOverlay() {
  const { getPositiveUniqueId } = useUniqueIds();

  return {
    ...OVERLAY_TEMPLATE,
    id: getPositiveUniqueId()
  };
}

function getIndicatorsCopyWithResetColors(indicators: Indicator[]) {
  if (!Array.isArray(indicators)) {
    return [];
  }

  const indicatorsCopy = cloneProxyObject(indicators)
    .filter(indicator => !!indicator);

  for (const indicator of indicatorsCopy) {
    indicator.indicatorColor = UP_DOWN_COLOR_OPTION_AUTO;
  }

  return indicatorsCopy;
}

function getOverlaysCopyWithResetColors(overlays: Overlay[]) {
  if (!Array.isArray(overlays)) {
    return [];
  }

  const overlaysCopy = cloneProxyObject(overlays)
    .filter(overlay => !!overlay);

  for (const overlay of overlaysCopy) {
    overlay.overlayColor = UP_DOWN_COLOR_OPTION_AUTO;
  }

  return overlaysCopy;
}

function formatIndicatorAndOverlayParameters(parameters: string) {
  if (typeof parameters !== 'string') {
    return '';
  }

  return parameters.replace(/\s/g, '').toUpperCase();
}

function formatSymbolParameter(symbolParameter: string): string {
  if (!symbolParameter) {
    return DEFAULT_INDICATOR_AND_OVERLAY_SYMBOL;
  }

  const containsColon = symbolParameter.indexOf(':') > -1;

  if (!containsColon) {
    return symbolParameter
      .toLowerCase()
      .replace(/\.n$/g, '')
      .replace(/'/g, '')
      .replace(/"/g, '')
      .replace(/\.x/g, '')
      .replace(/%/g, '')
      .replace(/\.a/, '/a')
      .replace(/\.b/, '/b')
      .toUpperCase();
  }

  const individualSymbols = symbolParameter.split(':');
  const formattedIndividualSymbols = individualSymbols.map(symbol => formatSymbolParameter(symbol));

  return formattedIndividualSymbols.join(':');
}

function formatPriceIndicatorParameters(parameters: string) {
  const formattedPriceIndicatorParameters = formatIndicatorAndOverlayParameters(parameters);
  const indicatorParametersArray = formattedPriceIndicatorParameters.split(',');
  let newIndicatorParameters = formatSymbolParameter(indicatorParametersArray[0]);
  let isLogScale = false;
  let isInvertedScale = false;

  for (let i = 1; i < indicatorParametersArray.length; i++) {
    const indicatorParameter = indicatorParametersArray[i];

    if (indicatorParameter === 'L' || indicatorParameter.startsWith('LOG')) {
      isLogScale = true;
    } else if (indicatorParameter === 'I' || indicatorParameter === 'INV' || indicatorParameter.startsWith('INVERT')) {
      isInvertedScale = true;
    }
  }

  if (isLogScale) {
    newIndicatorParameters = `${newIndicatorParameters},log`;
  }

  if (isInvertedScale) {
    newIndicatorParameters = `${newIndicatorParameters},inverted`;
  }

  return newIndicatorParameters;
}

export function useIndicatorsAndOverlays() {
  return {
    MAXIMUM_NUMBER_OF_INDICATORS,
    MAXIMUM_NUMBER_OF_INDICATORS_FOR_FREE_USERS,
    MAXIMUM_NUMBER_OF_OVERLAYS,
    MAXIMUM_NUMBER_OF_OVERLAYS_FOR_FREE_USERS,
    getBlankIndicator,
    getBlankOverlay,
    getIndicatorsCopyWithResetColors,
    getOverlaysCopyWithResetColors,
    formatIndicatorAndOverlayParameters,
    formatPriceIndicatorParameters
  };
}
